import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Stack,
  InputAdornment,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Checkbox,
  FormControlLabel,
  Popover
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  CalendarToday,
  Clear,
  Add,
  Refresh,
  ViewColumn
} from '@mui/icons-material';
import { investorApi } from '../../api/api';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const DocumentRequests = () => {
  const navigate = useNavigate();
  const [siteVisits, setSiteVisits] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState("All");
  const [propertyTypeFilter, setPropertyTypeFilter] = useState("All");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [pageSize, setPageSize] = useState(25);
  const [anchorEl, setAnchorEl] = useState(null);

  // Status configuration with colors
  const statusConfig = {
    Pending: { color: "warning", label: "Pending" },
    Completed: { color: "success", label: "Completed" }
  };

  // Property type configuration
  const propertyTypeConfig = {
    Property: { color: "primary", label: "Property" },
    Project: { color: "success", label: "Project" },
    Apartment: { color: "info", label: "Apartment" },
    "Individual Villa": { color: "secondary", label: "Individual Villa" }
  };

  const handleIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAction = (action, row) => {
    if (action === "view") {
      if (row.PropertyType === "Property" || row.PropertyType === "Individual Villa") {
        navigate(`/property/viewProperty?propertyId=${row.Pid}`, {
          state: {
            PropertyId: row.Pid,
            PropertyName: row.PropertyName,
            City: row.City,
            PropertyType: row.PropertyType,
            ProjectName: row.ProjectName,
            Address: row.Address,
            SizeInSqFt: row.SizeInSqFt,
            UnitPrice: row.UnitPrice,
            IconUrl: row.IconUrl,
          },
        });
      } else if (row.PropertyType === "Project") {
        navigate(`/viewProject/viewProject?projectId=${row.ProjectId}`, {
          state: {
            ProjectId: row.ProjectId,
            ProjectName: row.ProjectName,
            City: row.City,
            Address: row.Address,
            IconUrl: row.IconUrl,
          },
        });
      } else if (row.PropertyType === "Apartment") {
        navigate(`/apartment/viewApartment?propertyId=${row.ApartmentId}`, {
          state: {
            PropertyId: row.ApartmentId,
            PropertyType: row.PropertyType
          }
        });
      }
    } else if (action === "add") {
      let propertyId = '';
      let propertyName = '';

      // Determine the correct ID and name based on property type
      switch (row.PropertyType) {
        case "Property":
        case "Individual Villa":
          propertyId = row.Pid;
          propertyName = row.PropertyName;
          break;
        case "Apartment":
          propertyId = row.ApartmentId;
          propertyName = row.PropertyName || row.ApartmentName;
          break;
        case "Project":
          propertyId = row.ProjectId;
          propertyName = row.ProjectName;
          break;
        default:
          showSnackbar("Invalid property type", "error");
          return;
      }

      navigate(`/addEditableDocuments?propertyId=${propertyId}`, {
        state: {
          PropertyId: propertyId,
          propertyName: propertyName,
          City: row.City,
          type: row.PropertyType
        },
      });
    }
  };

  const fetchDocumentRequests = async () => {
    setLoading(true);
    try {
      const res = await investorApi.getAllDocumentRequests();
      const data = res.data || [];
      setSiteVisits(data);
      setFilteredVisits(data);
    } catch (error) {
      console.error('Failed to fetch document requests:', error);
      showSnackbar("Failed to load document requests", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocumentRequests();
  }, []);

  useEffect(() => {
    let filtered = siteVisits;

    // Filter by date
    if (filterDate) {
      filtered = filtered.filter((visit) => {
        if (!visit.request_date || isNaN(new Date(visit.request_date))) return false;
        const visitDate = dayjs(visit.request_date).format('YYYY-MM-DD');
        return visitDate === filterDate;
      });
    }

    // Filter by search keyword
    if (searchKeyword.trim() !== '') {
      const keyword = searchKeyword.toLowerCase();
      filtered = filtered.filter(
        (visit) =>
          visit.UserName?.toLowerCase().includes(keyword) ||
          visit.PropertyName?.toLowerCase().includes(keyword) ||
          visit.ApartmentName?.toLowerCase().includes(keyword) ||
          visit.ProjectName?.toLowerCase().includes(keyword) ||
          visit.MobileNumber?.toString().includes(keyword) ||
          visit.Email?.toLowerCase().includes(keyword)
      );
    }

    // Filter by status
    if (statusFilter && statusFilter !== 'All') {
      filtered = filtered.filter(
        (visit) => visit.Status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Filter by property type
    if (propertyTypeFilter && propertyTypeFilter !== 'All') {
      filtered = filtered.filter(
        (visit) => visit.PropertyType === propertyTypeFilter
      );
    }

    setFilteredVisits(filtered);
  }, [filterDate, searchKeyword, siteVisits, statusFilter, propertyTypeFilter]);

  const renderCellWithFallback = (params) => {
    return params.value === null || params.value === undefined || params.value === ''
      ? '------'
      : params.value;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '------';
    return dayjs(dateString).format('DD MMM YYYY');
  };

  const columns = [
    {
      field: 'Id',
      headerName: 'ID',
      width: 70,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'UserName',
      headerName: 'USER NAME',
      width: 180,
      renderCell: renderCellWithFallback
    },
    {
      field: 'ApartmentName',
      headerName: 'APARTMENT',
      width: 180,
      renderCell: renderCellWithFallback
    },
    {
      field: 'ProjectName',
      headerName: 'PROJECT',
      width: 180,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: "primary.main",
            cursor: "pointer",
            fontWeight: params.value ? 600 : 'normal',
            '&:hover': { textDecoration: params.value ? 'underline' : 'none' }
          }}
          onClick={() => params.value && handleAction("view", params.row)}
        >
          {renderCellWithFallback(params)}
        </Typography>
      )
    },
    {
      field: 'PropertyName',
      headerName: 'PROPERTY',
      width: 180,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
             color: "primary.main",
            cursor: "pointer",
            fontWeight: params.value ? 600 : 'normal',
            '&:hover': { textDecoration: params.value ? 'underline' : 'none' }
          }}
          onClick={() => params.value && handleAction("view", params.row)}
        >
          {renderCellWithFallback(params)}
        </Typography>
      )
    },
    {
      field: 'PropertyType',
      headerName: 'TYPE',
      width: 140,
      renderCell: (params) => {
        const typeInfo = propertyTypeConfig[params.value];
        return typeInfo ? (
          <Chip
            label={typeInfo.label}
            color={typeInfo.color}
            variant="outlined"
            size="small"
          />
        ) : (
          renderCellWithFallback(params)
        );
      }
    },
    {
      field: 'MobileNumber',
      headerName: 'MOBILE',
      width: 150,
      renderCell: params => (
        <Typography sx={{ color: '#0871c5' }}>
          {params.value || '------'}
        </Typography>
      ),
    },
    {
      field: 'Email',
      headerName: 'EMAIL',
      width: 220,
      renderCell: params => (
        <Typography sx={{ color: '#0871c5' }}>
          {params.value || '------'}
        </Typography>
      ),
    },
    {
      field: 'Status',
      headerName: 'STATUS',
      width: 130,
      renderCell: (params) => {
        const config = statusConfig[params.value] || { color: "default", label: params.value };
        return (
          <Chip
            label={config.label}
            color={config.color}
            variant="filled"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        );
      }
    },
    {
      field: 'request_date',
      headerName: 'REQUEST DATE',
      width: 150,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="Add Documents">
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => handleAction('add', params.row)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Add Docs
          </Button>
        </Tooltip>
      ),
    }
  ];

  const [selectedFields, setSelectedFields] = useState(
    columns.map((col) => col.field)
  );
  const displayedColumns = columns.filter((col) =>
    selectedFields.includes(col.field)
  );

  const clearFilters = () => {
    setFilterDate('');
    setSearchKeyword('');
    setStatusFilter('All');
    setPropertyTypeFilter('All');
  };

  const hasActiveFilters = filterDate || searchKeyword || statusFilter !== 'All' || propertyTypeFilter !== 'All';

  const open = Boolean(anchorEl);
  const id = open ? "column-popover" : undefined;

  return (
    <Box sx={{ marginTop: 2 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
          Document Requests
        </Typography>

      </Box>
      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
            <Typography variant="h4" fontWeight="bold" color="primary">
              {siteVisits.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Requests
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
            <Typography variant="h4" fontWeight="bold" color="warning.main">
              {siteVisits.filter(v => v.Status === 'Pending').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pending
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {siteVisits.filter(v => v.Status === 'Completed').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completed
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
            <Typography variant="h4" fontWeight="bold" color="info.main">
              {filteredVisits.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Filtered
            </Typography>
          </Card>
        </Grid>
      </Grid>
      {/* Header */}
      <Card
        sx={{
          flexGrow: 1,
          border: "1px solid lightgray",
          borderRadius: "5px",
          marginBottom: 2
        }}
      >
        <CardContent>
          <Grid
            container
            spacing={2}
            alignItems="center"
            direction="row"
            sx={{
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            {/* Search Field */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Search By Key words"
                variant="outlined"
                fullWidth
                size="small"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                }}
              />
            </Grid>

            {/* Date Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Date"
                type="date"
                variant="outlined"
                size="small"
                fullWidth
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="All">All Status</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Property Type Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Property Type</InputLabel>
                <Select
                  value={propertyTypeFilter}
                  onChange={(e) => setPropertyTypeFilter(e.target.value)}
                  label="Property Type"
                >
                  <MenuItem value="All">All Types</MenuItem>
                  <MenuItem value="Property">Property</MenuItem>
                  <MenuItem value="Project">Project</MenuItem>
                  <MenuItem value="Apartment">Apartment</MenuItem>
                  <MenuItem value="Individual Villa">Individual Villa</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Clear Filters Button */}
            <Grid item xs={12} sm={6} md={2}>
              <IconButton
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                sx={{
                  color: hasActiveFilters ? '#1976d2' : '#ccc',
                  border: '1px solid',
                  borderColor: hasActiveFilters ? '#1976d2' : '#ccc',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: hasActiveFilters ? '#e3f2fd' : 'transparent'
                  }
                }}
              >
                <Clear />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Clear Filters
                </Typography>
              </IconButton>
            </Grid>

            {/* Icon Button (Right end) */}
            <Grid item xs={12} sm="auto" md="auto">
              <IconButton onClick={handleIconClick}>
                <ViewColumn />
              </IconButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>



      {/* Data Grid */}
      <Card style={{ height: "100vh", overflowX: "hidden", marginTop: "10px", backgroundColor: "#f4f1f1ff" }}>
        <Box sx={{ height: 600, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={filteredVisits}
              columns={displayedColumns}
              pageSize={pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onPageSizeChange={(newSize) => setPageSize(newSize)}
              disableSelectionOnClick
              getRowId={(row) => row.Id}
              sx={{
                height: 650,
                border: '1px solid #ccc',
                borderRadius: 2,
                fontSize: '0.9rem',
                backgroundColor: '#ffffff',
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f0f0f0ff',
                },
                '& .MuiDataGrid-cell': {
                  color: '#333',
                  borderBottom: '1px solid #ddd',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f0f0f0ff',
                  color: 'black',
                  fontWeight: 'bold',
                },
                '& .MuiTablePagination-root': {
                  fontSize: '0.875rem',
                },
              }}
            />
          )}
        </Box>
      </Card>

      {/* Column Selection Popover */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="subtitle1" gutterBottom>
            Select Columns
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {columns.map((col) => (
              <FormControl
                key={col.field}
                component="fieldset"
                sx={{ m: 0, p: 0 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedFields.includes(col.field)}
                      onChange={() => {
                        setSelectedFields((prev) =>
                          prev.includes(col.field)
                            ? prev.filter((f) => f !== col.field)
                            : [...prev, col.field]
                        );
                      }}
                      sx={{
                        '&.Mui-checked': {
                          color: 'skyblue',
                        },
                      }}
                    />
                  }
                  label={col.headerName}
                  sx={{ m: 0 }}
                />
              </FormControl>
            ))}
          </Box>
        </Box>
      </Popover>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentRequests;